import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useKpiTargetsStore } from "../hooks";
import MetricCard from "./MetricCard";

const TargetSettingForm = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    getQuantitativeDisclosuresByCategory,
    selectedFramework,
    targetSearchTerm,
  } = useKpiTargetsStore();
  const searchTerm = targetSearchTerm.trim().toLowerCase();
  const disclosures = getQuantitativeDisclosuresByCategory(
    selectedFramework,
    activeCategory,
  );

  const filtered = searchTerm
    ? disclosures.filter((disclosure) => {
        const haystack = [
          disclosure.code,
          disclosure.title,
          disclosure.subEntryTitle,
          disclosure.category,
          disclosure.framework,
          disclosure.parentCode,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(searchTerm);
      })
    : disclosures;

  if (!disclosures || disclosures.length === 0) {
    return (
      <div className="rounded-radius border border-dashed border-border bg-muted/30 p-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t(
            "No quantitative disclosures found for {{category}} in {{framework}}.",
            {
              category: activeCategory,
              framework:
                selectedFramework === "All"
                  ? t("any framework")
                  : selectedFramework,
            },
          )}
        </p>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="rounded-radius border border-dashed border-border bg-muted/30 p-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t("No results for")}{" "}
          <span className="font-semibold text-foreground">
            "{targetSearchTerm}"
          </span>{" "}
          {t("in {{category}}", { category: activeCategory })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          {activeCategory === "All"
            ? t("All Disclosures")
            : t("{{category}} Disclosures", { category: activeCategory })}{" "}
          ({filtered.length})
        </h3>
        <p className="text-sm text-muted-foreground">
          {selectedFramework === "All"
            ? t("All frameworks")
            : selectedFramework}{" "}
          · {t("Set targets for quantitative disclosures")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {filtered.map((disclosure) => (
          <MetricCard
            key={`${disclosure.framework}_${disclosure.code}_${disclosure.subEntryIndex ?? "root"}`}
            disclosure={disclosure}
          />
        ))}
      </div>
    </div>
  );
});

export default TargetSettingForm;
